import {useState} from 'react'
import {ViewMode} from './utils/view-mode'
import {getCurrentUserLogin} from '@github-ui/client-env'

/**
 * The state values of the comments filter
 */
export interface CommentsFilterState {
  hideResolvedThreads: boolean
  hideOutdatedThreads: boolean
  filterText: string
  selectedAuthors?: string[]
  showAgents?: boolean
}

interface UseCommentsFilterStateResult {
  filterState: CommentsFilterState
  /** Set the filter state for the current view mode. */
  setCurrentFilterState: React.Dispatch<React.SetStateAction<CommentsFilterState>>
  /** Reset filters for every view mode. */
  resetFilterStates: () => void
}

export const defaultFilterStates = (): Record<ViewMode, CommentsFilterState> => {
  const currentUserLogin = getCurrentUserLogin()

  return {
    // "conversation" / "all"
    [ViewMode.IssueComments]: {
      filterText: '',
      hideResolvedThreads: false,
      hideOutdatedThreads: false,
      showAgents: true,
    },
    // "review" / "unresolved"
    [ViewMode.ThreadPreviews]: {
      filterText: '',
      hideResolvedThreads: true,
      hideOutdatedThreads: false,
      showAgents: true,
    },
    // "mine"
    [ViewMode.Mine]: {
      filterText: '',
      hideResolvedThreads: false,
      hideOutdatedThreads: false,
      selectedAuthors: currentUserLogin ? [currentUserLogin] : undefined,
      showAgents: true,
    },
  }
}

/** Tracks the state of comments filters. */
export function useCommentsFilterState(viewMode: ViewMode): UseCommentsFilterStateResult {
  // filter state for each view mode is tracked separately
  const [filterStates, setFilterStates] = useState(defaultFilterStates)

  const setViewModeFilterState = (mode: ViewMode, updatedState: React.SetStateAction<CommentsFilterState>) =>
    setFilterStates(previous => {
      const newState = typeof updatedState === 'function' ? updatedState(previous[mode]) : updatedState
      return {...previous, [mode]: newState}
    })

  const setCurrentFilterState = (filterState: React.SetStateAction<CommentsFilterState>) =>
    setViewModeFilterState(viewMode, filterState)

  return {
    filterState: filterStates[viewMode],
    setCurrentFilterState,
    resetFilterStates: () => setFilterStates(defaultFilterStates()),
  } as const
}
